/**
 * What a basket costs and what it can do — ROADMAP 9.66.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * THREE FIGURES, THREE DIFFERENT THINGS THEY NEED
 *
 * They are computed separately and refused separately, because a basket can
 * perfectly well be in a state where one is knowable and another is not — and
 * blanking all three because one input is missing hides answers we have.
 *
 *   NET PREMIUM   needs a price on every leg, and nothing else. It works
 *                 across expiries: what the basket collects or pays is a sum.
 *
 *   THE PAYOFF    needs prices, and needs every leg to expire TOGETHER. "Max
 *   max P / max L  profit at expiry" has no meaning for a calendar spread —
 *   breakevens     the near leg is cash and the far leg is still an option on
 *                 the day the curve is drawn for. Refused by name rather
 *                 than drawn wrong.
 *
 *   MARGIN        needs a solved implied volatility per leg, which comes from
 *                 the chain's greeks pass — so it needs the ladder to be
 *                 showing the expiry the basket trades. The honest answer
 *                 when it is not is "switch the chain to that date", which is
 *                 one click and is what the caller offers.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * PRICED AT THE TOUCH THE ORDER WILL CROSS
 *
 * A sold leg is filled on the BID and a bought leg on the ASK — the same rule
 * `netPremiumOf` and `payoffLegsOf` already use. Pricing the basket at the
 * last trade would put the breakevens in a slightly better place than the
 * trade can reach, every time, on the screen used to decide whether to take
 * it.
 * ─────────────────────────────────────────────────────────────────────────────
 */

import type { ResolvedLeg } from '@apna/shared'
import type { BasketLegDto } from '@/types/basket'
import type { MarginableLeg } from '@/screens/terminal/marginFor'

/** A quote, as the tick store answers one. */
export interface LegQuote {
  readonly ltp: number
  readonly bid?: number
  readonly ask?: number
}

/**
 * The basket as `ResolvedLeg`s, which is the shape every priced helper in the
 * product already takes — `netPremiumOf`, `payoffLegsOf` and the margin
 * adapter. Converting once here is what lets this screen reuse all three
 * rather than growing its own arithmetic.
 */
export function resolvedLegsOf(
  legs: readonly BasketLegDto[],
  quoteOf: (token: number) => LegQuote | null,
): ResolvedLeg[] {
  return legs.map((leg) => {
    const q = quoteOf(leg.token)
    return {
      token: leg.token,
      side: leg.side,
      optionType: leg.optionType,
      strike: leg.strike,
      lots: leg.lots,
      ltp: q?.ltp ?? 0,
      ...(q?.bid === undefined ? {} : { bid: q.bid }),
      ...(q?.ask === undefined ? {} : { ask: q.ask }),
    }
  })
}

/** The same legs as the margin scan takes them, priced at the crossing side. */
export function marginableLegsOf(legs: readonly ResolvedLeg[]): MarginableLeg[] {
  return legs.map((l) => ({
    token: l.token,
    side: l.side,
    optionType: l.optionType,
    strike: l.strike,
    lots: l.lots,
    price: (l.side === 'SELL' ? l.bid : l.ask) ?? l.ltp,
  }))
}

/**
 * The one expiry this basket trades, or null when it spans several.
 *
 * Null is the ANSWER for a calendar spread, not a failure to compute one —
 * the callers above treat it as "the expiry figures do not apply here", which
 * is different from "we could not work it out".
 */
export function soleExpiry(legs: readonly BasketLegDto[]): string | null {
  if (legs.length === 0) return null
  const first = legs[0]!.expiry
  return legs.every((l) => l.expiry === first) ? first : null
}

/** How many distinct expiries the basket touches. For the sentence that says so. */
export function expiryCount(legs: readonly BasketLegDto[]): number {
  return new Set(legs.map((l) => l.expiry)).size
}

/**
 * Lots, as a signed total per side — the one-line summary above the legs.
 *
 * Counted in LOTS rather than in legs because two legs of one lot and one leg
 * of two are the same exposure and read the same way on the ladder.
 */
export function basketSides(legs: readonly BasketLegDto[]): {
  buyLots: number
  sellLots: number
} {
  let buyLots = 0
  let sellLots = 0
  for (const l of legs) {
    if (l.side === 'BUY') buyLots += l.lots
    else sellLots += l.lots
  }
  return { buyLots, sellLots }
}
